import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import useApi from '../hooks/useApi';
import { useSettings } from '../context/SettingsContext';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import Combobox from '../components/ui/Combobox';
import Textarea from '../components/ui/Textarea';
import ConfirmDialog from '../components/ui/ConfirmDialog';
import RichTextEditor from '../components/ui/RichTextEditor';
import { useAuth } from '../context/AuthContext';
import { 
  ArrowLeft, Save, FileText, User, MapPin, 
  Calendar, Tag, AlertTriangle, Info, Truck, Link as LinkIcon, Printer, Paperclip,
  CheckCircle, Shield, Clock, Send, Download, Trash2, Eye, Sparkles, Files, Link2, X
} from 'lucide-react';
import toast from 'react-hot-toast';
import Badge from '../components/ui/Badge';
import ContactCombobox from '../components/shared/ContactCombobox';
import CaseHistory from '../components/CaseHistory';
import FilePreviewModal from '../components/FilePreviewModal';
import CaseSearchDropdown from '../components/CaseSearchDropdown';
import { getStoredKeys, signData } from '../utils/crypto';

const ReceiptForm = () => {
  const { id } = useParams();
  const isEdit = id && id !== 'new';
  const navigate = useNavigate();
  const { get, post, put, del } = useApi();
  const { settings } = useSettings();
  const isEfileEnabled = settings?.efile_enabled == 1 || settings?.efile_enabled === 'true' || settings?.efile_enabled === true;
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [existingFiles, setExistingFiles] = useState([]);
  const [previewFile, setPreviewFile] = useState(null);
  const [initialPage, setInitialPage] = useState(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [hasKeys, setHasKeys] = useState(false);
  const [shouldSign, setShouldSign] = useState(false);
  const [isAddressReadOnly, setIsAddressReadOnly] = useState(false);
  const [referenceCases, setReferenceCases] = useState([]);
  const [referenceLoading, setReferenceLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const steps = [
    { key: 'details', label: 'Diary Details' },
    { key: 'receiving', label: 'Receiving Details' },
  ];
  const stepsLength = steps.length;

  const escapeRegex = (value) => String(value).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const buildNumberValidation = (format, officeCode) => {
    const fmt = String(format || '').trim();
    if (!fmt) return { example: '', regex: null };
    const office = officeCode || '';
    const example = fmt
      .replaceAll('{OFFICE}', office || 'OFFICE')
      .replaceAll('{YYYY}', String(new Date().getFullYear()))
      .replaceAll('{SEQ}', '0001');
    const tokens = fmt.split(/(\{OFFICE\}|\{YYYY\}|\{SEQ\})/g).filter(Boolean);
    const pattern = tokens.map((t) => {
      if (t === '{OFFICE}') return office ? escapeRegex(office) : '[A-Z0-9_-]+';
      if (t === '{YYYY}') return '\\d{4}';
      if (t === '{SEQ}') return '\\d{4}';
      return escapeRegex(t);
    }).join('');
    return { example, regex: new RegExp(`^${pattern}$`, 'i') };
  };

  const receiptNumberFormat = settings?.receipt_number_format || 'WAPDA/RCPT/{YYYY}/{SEQ}';
  const diaryNumberValidation = buildNumberValidation(receiptNumberFormat, user?.officeCode);
  const numberFieldPattern = (diaryNumberValidation.regex?.source || '').replace(/^\^/, '').replace(/\$$/, '');
  const sanitizeNumberField = (raw) => String(raw || '').replace(/[^A-Za-z0-9/_() .&-]/g, '');

  const handleInitiateGreenSheet = () => {
    setConfirmDialog({
      isOpen: true,
      title: 'Initiate Green Sheet',
      message: 'Are you sure you want to initiate a Green Sheet for this receipt? This will create a new e-File.',
      confirmText: 'Initiate',
      variant: 'primary',
      onConfirm: async () => {
        setLoading(true);
        try {
          const res = await post(`/receipt/${id}/put-in-efile`);
          if (res.success) {
            toast.success('Green Sheet initiated successfully');
            navigate(`/file/${res.data.eFileId}`);
          }
        } catch (error) {
          console.error(error);
          toast.error(error.response?.data?.message || 'Failed to initiate Green Sheet');
        } finally {
          setLoading(false);
        }
      }
    });
  };

  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    title: '',
    message: '',
    confirmText: 'Confirm',
    variant: 'danger',
    onConfirm: () => {}
  });

  const [formData, setFormData] = useState({
    diaryNumber: '',
    documentType: 'Letter',
    subject: '',
    sender: '',
    address: '',
    category: 'General',
    receiptDate: new Date().toISOString().split('T')[0],
    mode: 'Post',
    priority: 'Normal',
    status: 'In Progress',
    remarks: '',
    referenceNumber: '',
    dateOfLetter: '',
    distributedTo: '',
    distributedToDesignation: '',
    distributedToDept: '',
    isConfidential: false,
    confidentialityLevel: 'Normal'
  });

  const canEdit = true;
  const draftKey = user?.userId ? `receiptFormDraft:${user.userId}` : 'receiptFormDraft';

  const clearDraft = () => {
    try {
      sessionStorage.removeItem(draftKey);
    } catch {
      void 0;
    }
  };

  useEffect(() => {
    if (isEdit) return;
    if (!user?.userId) return;
    try {
      const raw = sessionStorage.getItem(draftKey);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== 'object') return;
      if (parsed.formData && typeof parsed.formData === 'object') {
        setFormData(prev => ({ ...prev, ...parsed.formData }));
      }
      if (Number.isFinite(parsed.currentStep)) {
        setCurrentStep(Math.max(0, Math.min(Number(parsed.currentStep), stepsLength - 1)));
      }
    } catch {
      void 0;
    }
  }, [isEdit, user?.userId, stepsLength, draftKey]);

  useEffect(() => {
    if (isEdit) return;
    if (!user?.userId) return;
    try {
      sessionStorage.setItem(draftKey, JSON.stringify({ formData, currentStep }));
    } catch {
      void 0;
    }
  }, [isEdit, user?.userId, formData, currentStep, draftKey]);

  useEffect(() => {
    if (isEdit) {
      fetchReceipt();
    }
    checkKeys();
  }, [id]);

  useEffect(() => {
    if (isEdit) return;
    const list = Array.isArray(settings?.receipt_document_types) ? settings.receipt_document_types : [];
    if (list.length === 0) return;
    setFormData(prev => {
      const current = String(prev.documentType || '').trim();
      if (current && list.includes(current)) return prev;
      return { ...prev, documentType: list[0] };
    });
  }, [isEdit, settings?.receipt_document_types]);

  const checkKeys = async () => {
    const keys = await getStoredKeys();
    if (keys) {
      setHasKeys(true);
      setShouldSign(true);
    }
  };

  const fetchReceipt = async () => {
    try {
      const response = await get(`/receipt/${id}`);
      setFormData(prev => ({
        ...prev,
        ...response.data,
        receiptDate: response.data.dateReceived ? String(response.data.dateReceived).substring(0, 10) : prev.receiptDate,
        address: response.data.senderAddress ?? response.data.address ?? prev.address,
        category: response.data.category || 'General',
        mode: response.data.mode || 'Post',
        documentType: response.data.documentType || prev.documentType || '',
        priority: response.data.priority || 'Normal',
        status: response.data.status || 'In Progress',
        isConfidential: response.data.isConfidential === 1 || response.data.isConfidential === true,
      }));
      setExistingFiles(response.data.attachments || []);
    } catch (error) {
      toast.error('Failed to load diary details');
      navigate('/diary');
    }
  };


  const handleChange = (e) => {
    const { name, value } = e.target;
    const nextValue = (name === 'diaryNumber' && !isEdit && settings.receipt_number_scheme === 'manual')
      ? sanitizeNumberField(value)
      : value;
    setFormData(prev => ({ ...prev, [name]: nextValue }));
    // Clear error when user types
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const handleFileChange = (e) => {
    setSelectedFiles(Array.from(e.target.files));
  };

  const handleDownload = async (file) => {
    try {
      const blob = await get(`/files/${file.fileName}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', file.originalName);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download file');
    }
  };

  const handlePreview = (file) => {
    setPreviewFile(file);
    setInitialPage(null);
    setIsPreviewOpen(true);
  };

  const handlePageClick = (page) => {
    const pdfFile = existingFiles.find(f => f.mimeType === 'application/pdf');
    if (pdfFile) {
      setPreviewFile(pdfFile);
      setInitialPage(page);
      setIsPreviewOpen(true);
    } else {
      toast.error('No PDF attachment found to reference.');
    }
  };

  const handleDownloadBundle = async () => {
    try {
      const pdfFiles = existingFiles.filter(f => f.mimeType === 'application/pdf');
      if (pdfFiles.length === 0) return;
      
      const fileIds = pdfFiles.map(f => f.id);
      const bundleName = `Bundle_${formData.diaryNumber || 'Receipt'}.pdf`;
      
      const response = await post('/pdf/bundle', { fileIds, bundleName }, { responseType: 'blob' });
      
      const url = window.URL.createObjectURL(new Blob([response]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', bundleName);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      toast.success('PDF Bundle downloaded');
    } catch (e) {
      console.error("Bundle failed", e);
    }
  };

  const getCaseEndpoint = (type, caseId) => {
    if (type === 'dispatch') return `/dispatch/${caseId}`;
    return `/receipt/${caseId}`;
  };

  const fetchCaseAttachments = async (type, caseId) => {
    const response = await get(getCaseEndpoint(type, caseId));
    return Array.isArray(response?.data?.attachments) ? response.data.attachments : [];
  };

  const handleSelectReferenceCase = async (item) => {
    if (!item?.id || !item?.type) return;
    const normalizedType = String(item.type || '').toLowerCase();
    if (!['receipt', 'dispatch'].includes(normalizedType)) return;

    const exists = referenceCases.some((c) => c.type === normalizedType && String(c.id) === String(item.id));
    if (exists) {
      toast('Case already added');
      return;
    }

    setReferenceLoading(true);
    try {
      const attachments = await fetchCaseAttachments(normalizedType, item.id);
      setReferenceCases((prev) => [
        ...prev,
        {
          id: item.id,
          type: normalizedType,
          number: item.number || '-',
          subject: item.subject || '-',
          attachments
        }
      ]);
      toast.success('Reference case added');
    } catch (error) {
      console.error('Failed to load selected case attachments', error);
      toast.error('Failed to load selected case attachments');
    } finally {
      setReferenceLoading(false);
    }
  };

  const handleRemoveReferenceCase = (caseId, caseType) => {
    setReferenceCases((prev) => prev.filter((c) => !(String(c.id) === String(caseId) && c.type === caseType)));
  };

  const handleDeleteFile = (fileId) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Delete File',
      message: 'Are you sure you want to delete this file?',
      confirmText: 'Delete',
      variant: 'danger',
      onConfirm: async () => {
        try {
          await del(`/files/${fileId}`);
          setExistingFiles(prev => prev.filter(f => f.id !== fileId));
          toast.success('File deleted successfully');
        } catch (error) {
          console.error('Delete error:', error);
          toast.error('Failed to delete file');
        }
      }
    });
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.subject.trim()) newErrors.subject = 'Subject is required';
    if (!formData.sender.trim()) newErrors.sender = 'Sender is required';
    if (!formData.receiptDate) newErrors.receiptDate = 'Diary Date is required';
    if (!String(formData.documentType || '').trim()) newErrors.documentType = 'Document Type is required';
    
    // Check for manual receipt number
    if (!isEdit && settings.receipt_number_scheme === 'manual' && !formData.diaryNumber?.trim()) {
      newErrors.diaryNumber = 'Diary Number is required in manual mode';
    } else if (!isEdit && settings.receipt_number_scheme === 'manual' && formData.diaryNumber?.trim()) {
      if (diaryNumberValidation.regex && !diaryNumberValidation.regex.test(formData.diaryNumber.trim())) {
        newErrors.diaryNumber = `Invalid diary number format. Example: ${diaryNumberValidation.example}`;
      }
    }

    if (formData.isConfidential && (!formData.confidentialityLevel || formData.confidentialityLevel === 'Normal')) {
      newErrors.confidentialityLevel = 'Please select a confidentiality level';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validateForm()) {
      toast.error('Please fix the errors in the form');
      return;
    }

    setConfirmDialog({
      isOpen: true,
      title: isEdit ? 'Update Diary' : 'Create Diary',
      message: `Are you sure you want to ${isEdit ? 'update' : 'create'} this diary record?`,
      confirmText: isEdit ? 'Update' : 'Create',
      variant: 'primary',
      onConfirm: submitForm
    });
  };

  const validateStep = (stepIndex) => {
    const newErrors = {};
    if (stepIndex === 0) {
      if (!formData.subject.trim()) newErrors.subject = 'Subject is required';
      if (!formData.receiptDate) newErrors.receiptDate = 'Diary Date is required';
      if (!String(formData.documentType || '').trim()) newErrors.documentType = 'Document Type is required';
      if (!isEdit && settings.receipt_number_scheme === 'manual' && !formData.diaryNumber?.trim()) {
        newErrors.diaryNumber = 'Diary Number is required in manual mode';
      } else if (!isEdit && settings.receipt_number_scheme === 'manual' && formData.diaryNumber?.trim()) {
        if (diaryNumberValidation.regex && !diaryNumberValidation.regex.test(formData.diaryNumber.trim())) {
          newErrors.diaryNumber = `Invalid diary number format. Example: ${diaryNumberValidation.example}`;
        }
      }
    }
    if (stepIndex === 1) {
      if (!formData.sender.trim()) newErrors.sender = 'Sender is required';
    }
    setErrors(prev => ({ ...prev, ...newErrors }));
    const ok = Object.keys(newErrors).length === 0;
    if (!ok) toast.error('Please complete required fields');
    return ok;
  };

  const goNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep((s) => Math.min(s + 1, steps.length - 1));
    }
  };

  const goPrev = () => {
    setCurrentStep((s) => Math.max(s - 1, 0));
  };

  const submitForm = async () => {
    setLoading(true);
    try {
      let digitalSignature = null;
      
      if (shouldSign && hasKeys) {
          try {
              const keys = await getStoredKeys();
              if (keys && keys.privateKey) {
                  const dataToSign = JSON.stringify({
                      diaryNumber: formData.diaryNumber,
                      subject: formData.subject,
                      sender: formData.sender,
                      date: formData.receiptDate,
                      officeId: user?.officeId
                  });
                  digitalSignature = await signData(keys.privateKey, dataToSign);
                  toast.success('Digital Signature Generated');
              }
          } catch (err) {
              console.error('Signing failed', err);
              toast.error('Digital Signing failed, proceeding without signature');
          }
      }

      const data = new FormData();
      const payload = {
        ...formData,
        approvalStatus: 'Approved',
        dateReceived: formData.receiptDate,
        senderAddress: formData.address,
        isConfidential: formData.isConfidential ? 'true' : 'false',
        confidentialityLevel: formData.isConfidential ? (formData.confidentialityLevel || 'Restricted') : 'Normal'
      };
      Object.keys(payload).forEach(key => {
        // Handle null/undefined values
        if (key === 'receiptDate' || key === 'address') return;
        if (payload[key] !== null && payload[key] !== undefined) {
          data.append(key, payload[key]);
        }
      });
      
      if (digitalSignature) {
          data.append('digitalSignature', digitalSignature);
      }
      
      selectedFiles.forEach(file => {
        data.append('files', file);
      });

      if (isEdit) {
        await put(`/receipt/${id}`, data, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        toast.success('Diary updated successfully');
      } else {
        const createResponse = await post('/receipt', data, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });

        const newReceiptId = createResponse?.data?.id || createResponse?.id || createResponse?.data?.receipt?.id;
        if (referenceCases.length > 0 && newReceiptId) {
          const relationResults = await Promise.allSettled(
            referenceCases.map((c) =>
              post('/relations', {
                sourceType: 'receipt',
                sourceId: newReceiptId,
                targetType: c.type,
                targetId: c.id,
                relationType: 'Reference'
              })
            )
          );

          const failedCount = relationResults.filter((result) => result.status === 'rejected').length;
          if (failedCount === 0) {
            toast.success('Reference links created');
          } else {
            toast.error(`Diary created, but ${failedCount} reference link(s) failed`);
          }
        } else if (referenceCases.length > 0 && !newReceiptId) {
          toast.error('Diary created, but reference linking could not be completed');
        }
        toast.success('Diary created successfully');
      }
      if (!isEdit) clearDraft();
      navigate('/diary');
    } catch (error) {
      console.error('Failed to save diary', error);
      toast.error(error?.response?.data?.message || error?.message || 'Failed to save diary');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
     setConfirmDialog({
      isOpen: true,
      title: 'Cancel Changes',
      message: 'Are you sure you want to cancel? Unsaved changes will be lost.',
      confirmText: 'Yes, Cancel',
      variant: 'warning',
      onConfirm: () => {
        if (!isEdit) clearDraft();
        navigate('/diary');
      }
    });
  };

  const getAgingStatus = (data) => {
    if (!data.status || data.status === 'Closed' || data.status === 'Completed') return null;
    
    const now = new Date();
    
    // Check Acknowledgment Status (Orange Alert)
    if (data.forwardedTo && !data.isAcknowledged) {
       const assignedTime = new Date(data.forwardedAt || data.updatedAt);
       const diffHours = (now - assignedTime) / (1000 * 60 * 60);
       
       if (diffHours > 4) {
          return { color: 'text-orange-700', bg: 'bg-orange-100', icon: AlertTriangle, label: 'Unacknowledged > 4h' };
       }
    }
    
    // Check Processing Status (Red Alert)
    const lastUpdate = new Date(data.updatedAt || data.createdAt);
    const diffHours = (now - lastUpdate) / (1000 * 60 * 60);
    
    if (diffHours > 48) {
      return { color: 'text-red-700', bg: 'bg-red-100', icon: AlertTriangle, label: 'Open > 48h' };
    }
    
    return null;
  };

  const handleDownloadGreenSheet = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${import.meta.env.VITE_API_URL}/pdf/green-sheet/receipt/${id}?t=${Date.now()}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) throw new Error('Failed to generate Green Sheet');
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `GreenSheet_Receipt_${id}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Green Sheet downloaded successfully');
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download Green Sheet');
    } finally {
      setLoading(false);
    }
  };

  const aging = getAgingStatus(formData);
  const AgingIcon = aging ? aging.icon : null;

  return (
    <>
      <div className="max-w-7xl mx-auto mb-8 px-4">
        {formData.isConfidential && (
          <div className="mb-6 bg-white p-4 rounded-xl shadow-sm border border-gray-100 flex items-center justify-end">
            <div className="flex items-center gap-2 px-3 py-1 bg-red-50 text-red-700 rounded-full border border-red-100">
              <Shield className="w-4 h-4" />
              <span className="text-xs font-bold uppercase tracking-wider">Confidential</span>
            </div>
          </div>
        )}

        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between mb-6">
          <div className="flex items-center gap-3 sm:gap-4 min-w-0">
            <Button 
              variant="ghost" 
              onClick={() => navigate('/diary')}
              className="hover:bg-gray-100 rounded-full p-2 transition-colors"
            >
              <ArrowLeft className="w-6 h-6 text-gray-600" />
            </Button>
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 tracking-tight truncate">
                  {isEdit ? 'Edit Diary' : 'New Diary'}
                </h1>
                {isEdit && (
                  <div className="flex flex-wrap gap-2">
                    {formData.linkedEFileId && (
                      <Badge className="text-sm px-3 py-1 bg-blue-50 text-blue-700 border border-blue-200 flex items-center gap-1">
                        <LinkIcon className="w-3 h-3" />
                        Linked to Active File: {formData.linkedEFileNumber}
                      </Badge>
                    )}
                    <Badge className={`text-sm px-3 py-1 ${
                      formData.status === 'Closed' ? 'bg-green-100 text-green-800' : 
                      formData.status === 'In Progress' ? 'bg-yellow-100 text-yellow-800' : 
                      'bg-blue-100 text-blue-800'
                    }`}>
                      {formData.status}
                    </Badge>
                    {aging && (
                      <div className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium ${aging.bg} ${aging.color} border border-transparent`}>
                         <AgingIcon className="w-4 h-4" />
                         {aging.label}
                      </div>
                    )}
                  </div>
                )}
              </div>
              <p className="text-gray-500 mt-1">
                {isEdit ? 'Update existing diary record details' : 'Register a new incoming diary for tracking'}
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 sm:gap-3">
            {isEdit && (
              <Button 
                variant="secondary" 
                onClick={() => window.open(`/diary/${id}/view?print=1`, '_blank', 'noopener,noreferrer')}
                className="w-full sm:w-auto"
              >
                <Printer className="w-4 h-4 mr-2" />
                Print
              </Button>
            )}
            <Button 
              variant="secondary" 
              onClick={() => navigate('/diary')}
              className="w-full sm:w-auto"
            >
              Cancel
            </Button>

            {isEfileEnabled && isEdit && !formData.linkedEFileId && user?.userId === formData.forwardedTo && (
              <Button 
                variant="primary"
                className="w-full sm:w-auto"
                onClick={handleInitiateGreenSheet}
                disabled={loading}
              >
                <Files className="w-4 h-4 mr-2" />
                Initiate Green Sheet (Put up in e-File)
              </Button>
            )}

            <Button 
              variant="primary"
              onClick={handleSubmit} 
              disabled={loading || !canEdit}
              className="w-full sm:w-auto"
            >
              <Save className="w-4 h-4 mr-2" />
              {loading ? 'Saving...' : (isEdit ? 'Save Changes' : 'Create Diary')}
            </Button>
          </div>
        </div>

        <div className="mb-6 overflow-x-auto">
          <div className="bg-white rounded-xl border border-gray-100 p-4 shadow-sm">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
              <div className="flex gap-3 min-w-[420px] lg:min-w-0">
                {steps.map((s, i) => (
                  <div key={s.key} className="flex items-center">
                    <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm ${i === currentStep ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-700'}`}>
                      <span>{i + 1}</span>
                      <span className="font-medium">{s.label}</span>
                    </div>
                    {i < steps.length - 1 && <div className="w-8 h-px bg-gray-200 mx-2" />}
                  </div>
                ))}
              </div>
              <div className="flex items-center gap-2 lg:self-auto self-end">
                <Button variant="secondary" onClick={goPrev} disabled={currentStep === 0}>
                  Back
                </Button>
                {currentStep < steps.length - 1 ? (
                  <Button onClick={goNext}>
                    Next
                  </Button>
                ) : (
                  <Button onClick={handleSubmit} disabled={loading || !canEdit}>
                    {isEdit ? 'Save Changes' : 'Create Diary'}
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">

          {currentStep === 0 && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-50 bg-gray-50/50 flex items-center gap-3">
              <FileText className="w-5 h-5 text-indigo-500" />
              <h3 className="text-lg font-semibold text-gray-800">Diary Details</h3>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                <div className="col-span-2">
                  <Input
                    label="Diary Number"
                    id="diaryNumber"
                    name="diaryNumber"
                    icon={FileText}
                    required={settings.receipt_number_scheme === 'manual'}
                    value={formData.diaryNumber || ''}
                    onChange={handleChange}
                    onBlur={() => {
                      if (isEdit || settings.receipt_number_scheme !== 'manual') return;
                      const v = String(formData.diaryNumber || '').trim();
                      if (!v) return;
                      if (diaryNumberValidation.regex && !diaryNumberValidation.regex.test(v)) {
                        setErrors(prev => ({ ...prev, diaryNumber: `Invalid diary number format. Example: ${diaryNumberValidation.example}` }));
                      }
                    }}
                    error={errors.diaryNumber}
                    placeholder={settings.receipt_number_scheme === 'auto' && !isEdit ? "Auto-generated upon save" : (diaryNumberValidation.example ? `e.g. ${diaryNumberValidation.example}` : "Enter diary number")}
                    disabled={settings.receipt_number_scheme === 'auto' || isEdit}
                    className={settings.receipt_number_scheme === 'auto' && !isEdit ? "bg-gray-50 text-gray-500" : ""}
                    maxLength={80}
                    pattern={settings.receipt_number_scheme === 'manual' ? numberFieldPattern : undefined}
                    title={settings.receipt_number_scheme === 'manual' && diaryNumberValidation.example ? `Example: ${diaryNumberValidation.example}` : undefined}
                  />
                  {settings.receipt_number_scheme === 'auto' && !isEdit && (
                    <p className="text-xs text-gray-500 mt-1">Number will be automatically generated based on system settings.</p>
                  )}
                  {settings.receipt_number_scheme === 'manual' && !isEdit && (
                    <p className="text-xs text-gray-500 mt-1">Format: {receiptNumberFormat}</p>
                  )}
                </div>
                <div className="col-span-2">
                  <Input
                    label="Subject *"
                    id="subject"
                    name="subject"
                    icon={FileText}
                    required
                    value={formData.subject}
                    onChange={handleChange}
                    error={errors.subject}
                    placeholder="Subject of the correspondence"
                    className="transition-all duration-200 focus:ring-2 focus:ring-indigo-100"
                    disabled={!canEdit}
                  />
                </div>

                {!isEdit && (
                  <div className="col-span-2 rounded-lg border border-indigo-100 bg-indigo-50/40 p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <Link2 className="w-4 h-4 text-indigo-600" />
                      <h4 className="text-sm font-semibold text-indigo-900">Reference Case Attachments</h4>
                    </div>

                    <p className="text-xs text-indigo-800/80 mb-3">
                      Search an existing Diary/Dispatch and view attachments while creating this diary.
                    </p>

                    <CaseSearchDropdown
                      onSelect={handleSelectReferenceCase}
                      placeholder="Search case by number or subject to add reference attachments..."
                      includeEfileNotings={false}
                    />

                    {referenceLoading && <p className="mt-2 text-xs text-gray-500">Loading selected case attachments...</p>}

                    {referenceCases.length > 0 && (
                      <div className="mt-4 space-y-3">
                        {referenceCases.map((c) => (
                          <div key={`${c.type}-${c.id}`} className="rounded-md border border-indigo-100 bg-white p-3">
                            <div className="flex items-start justify-between gap-3">
                              <div className="min-w-0">
                                <div className="flex items-center gap-2">
                                  <Badge className="bg-blue-100 text-blue-800">{String(c.type || '').toUpperCase()}</Badge>
                                  <span className="text-sm font-semibold text-gray-900">{c.number}</span>
                                </div>
                                <p className="text-xs text-gray-600 mt-1 break-words [overflow-wrap:anywhere]" title={c.subject}>{c.subject}</p>
                              </div>
                              <button
                                type="button"
                                onClick={() => handleRemoveReferenceCase(c.id, c.type)}
                                className="p-1 text-gray-400 hover:text-red-600 rounded"
                                title="Remove reference"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </div>

                            <div className="mt-3">
                              {Array.isArray(c.attachments) && c.attachments.length > 0 ? (
                                <ul className="space-y-2">
                                  {c.attachments.map((file, index) => (
                                    <li key={`${c.type}-${c.id}-${file.id || index}`} className="flex items-center justify-between gap-2 rounded border border-gray-200 bg-gray-50 px-2 py-1.5">
                                      <div className="min-w-0 flex items-center gap-2">
                                        <FileText className="w-4 h-4 text-gray-500 flex-shrink-0" />
                                        <span className="text-xs text-gray-700 break-words [overflow-wrap:anywhere]" title={file.originalName}>{file.originalName}</span>
                                      </div>
                                      <div className="flex items-center gap-1">
                                        {(file.mimeType === 'application/pdf' || String(file.mimeType || '').startsWith('image/')) && (
                                          <button
                                            type="button"
                                            className="p-1 text-gray-500 hover:text-indigo-600"
                                            title="Preview"
                                            onClick={() => handlePreview(file)}
                                          >
                                            <Eye className="w-4 h-4" />
                                          </button>
                                        )}
                                        <button
                                          type="button"
                                          className="p-1 text-gray-500 hover:text-indigo-600"
                                          title="Download"
                                          onClick={() => handleDownload(file)}
                                        >
                                          <Download className="w-4 h-4" />
                                        </button>
                                      </div>
                                    </li>
                                  ))}
                                </ul>
                              ) : (
                                <p className="text-xs text-gray-500">No attachments found for this case.</p>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                <div className="md:col-span-2 p-4 bg-red-50 border border-red-100 rounded-md">
                  <div className="flex flex-col md:flex-row gap-4 md:items-end">
                    <label className="flex items-center gap-3 cursor-pointer select-none">
                      <input
                        type="checkbox"
                        id="isConfidential"
                        name="isConfidential"
                        checked={!!formData.isConfidential}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          setFormData(prev => ({
                            ...prev,
                            isConfidential: checked,
                            confidentialityLevel: checked ? (prev.confidentialityLevel && prev.confidentialityLevel !== 'Normal' ? prev.confidentialityLevel : 'Restricted') : 'Normal'
                          }));
                          if (errors.isConfidential || errors.confidentialityLevel) {
                            setErrors(prev => ({ ...prev, isConfidential: null, confidentialityLevel: null }));
                          }
                        }}
                        disabled={!canEdit}
                        className="w-4 h-4 text-red-600 border-gray-300 rounded focus:ring-red-500"
                      />
                      <span className="text-sm font-medium text-red-700">Confidential</span>
                    </label>

                    <div className={`flex-1 ${!formData.isConfidential ? 'opacity-60 pointer-events-none' : ''}`}>
                      <label htmlFor="confidentialityLevel" className="block text-sm font-medium text-red-700 mb-1">
                        Confidentiality Level
                      </label>
                      <Combobox
                        value={formData.confidentialityLevel || 'Normal'}
                        onChange={(val) => {
                          setFormData(prev => ({
                            ...prev,
                            confidentialityLevel: val,
                            isConfidential: val !== 'Normal'
                          }));
                          if (errors.confidentialityLevel) setErrors(prev => ({ ...prev, confidentialityLevel: null }));
                        }}
                        disabled={!canEdit || !formData.isConfidential}
                        options={[
                          { value: "Normal", label: "Normal" },
                          { value: "Restricted", label: "Restricted" },
                          { value: "Confidential", label: "Confidential" },
                          { value: "Secret", label: "Secret" },
                          { value: "Top Secret", label: "Top Secret" }
                        ]}
                        className="w-full"
                        placeholder="Select level"
                      />
                      {errors.confidentialityLevel && <p className="text-xs text-red-600 mt-1">{errors.confidentialityLevel}</p>}
                    </div>
                  </div>
                </div>

                {hasKeys && (
                  <div className="md:col-span-2 flex items-center gap-2 p-3 bg-green-50 border border-green-100 rounded-md">
                    <input
                      type="checkbox"
                      id="shouldSign"
                      name="shouldSign"
                      checked={shouldSign}
                      onChange={(e) => setShouldSign(e.target.checked)}
                      className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                    />
                    <label htmlFor="shouldSign" className="flex items-center gap-2 text-sm font-medium text-green-700 cursor-pointer select-none">
                      <Shield className="w-4 h-4" />
                      Digitally Sign
                    </label>
                  </div>
                )}

                <div>
                  <Combobox
                    label="Document Type *"
                    id="documentType"
                    name="documentType"
                    icon={FileText}
                    value={formData.documentType}
                    onChange={(val) => handleChange({ target: { name: 'documentType', value: val } })}
                    options={(settings.receipt_document_types || []).map(t => ({ value: t, label: t }))}
                    placeholder="Select Document Type"
                    error={errors.documentType}
                  />
                </div>

                <div>
                  <Combobox
                    label="Category"
                    id="category"
                    name="category"
                    icon={Tag}
                    value={formData.category}
                    onChange={(val) => handleChange({ target: { name: 'category', value: val } })}
                    options={(settings.receipt_categories || []).map(cat => ({ value: cat, label: cat }))}
                    placeholder="Select Category"
                  />
                </div>

                <div>
                  <Input
                    type="date"
                    label="Receipt Date *"
                    id="receiptDate"
                    name="receiptDate"
                    icon={Calendar}
                    required
                    value={formData.receiptDate}
                    onChange={handleChange}
                    error={errors.receiptDate}
                    className="transition-all duration-200 focus:ring-2 focus:ring-indigo-100"
                  />
                </div>

                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Attachments {isEdit && '(Add new files)'}
                  </label>
                  <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-lg hover:border-indigo-400 transition-colors">
                    <div className="space-y-1 text-center">
                      <div className="flex flex-col items-center">
                        <Paperclip className="mx-auto h-12 w-12 text-gray-400" />
                        <div className="flex text-sm text-gray-600">
                          <label
                            htmlFor="file-upload"
                            className="relative cursor-pointer bg-white rounded-md font-medium text-indigo-600 hover:text-indigo-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-indigo-500"
                          >
                            <span>Upload files</span>
                            <input 
                              id="file-upload" 
                              name="file-upload" 
                              type="file" 
                              className="sr-only" 
                              multiple
                              onChange={handleFileChange}
                            />
                          </label>
                          <p className="pl-1">or drag and drop</p>
                        </div>
                        <p className="text-xs text-gray-500">
                          PDF, DOC, DOCX, JPG, PNG up to 10MB
                        </p>
                      </div>

                      {existingFiles.length > 0 && (
                        <div className="mt-4 text-left border-t pt-4 w-full">
                          <div className="flex items-center justify-between mb-2">
                            <h4 className="text-sm font-medium text-gray-700">Attached Files:</h4>
                            {existingFiles.filter(f => f.mimeType === 'application/pdf').length > 1 && (
                              <button
                                type="button"
                                onClick={handleDownloadBundle}
                                className="text-xs flex items-center gap-1 text-indigo-600 hover:text-indigo-800 font-medium px-2 py-1 rounded hover:bg-indigo-50 transition-colors"
                                title="Download all PDFs as a single file"
                              >
                                <Files className="w-3.5 h-3.5" />
                                Bundle PDFs
                              </button>
                            )}
                          </div>
                          <ul className="space-y-2">
                            {existingFiles.map((file, index) => (
                              <li key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded-md border border-gray-200">
                                <div className="flex items-center truncate mr-2">
                                  <FileText className="w-4 h-4 text-gray-500 mr-2 flex-shrink-0" />
                                  <span className="text-sm text-gray-700 truncate" title={file.originalName}>{file.originalName}</span>
                                  <span className="text-xs text-gray-400 ml-2 whitespace-nowrap">({(file.fileSize / 1024 / 1024).toFixed(2)} MB)</span>
                                </div>
                                <div className="flex items-center gap-1">
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      handleDownload(file);
                                    }}
                                    className="text-indigo-600 hover:text-indigo-800 p-1 flex-shrink-0"
                                    title="Download"
                                  >
                                    <Download className="w-4 h-4" />
                                  </button>
                                  {canEdit && (
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        handleDeleteFile(file.id);
                                      }}
                                      className="text-red-600 hover:text-red-800 p-1 flex-shrink-0"
                                      title="Delete"
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </button>
                                  )}
                                </div>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {selectedFiles.length > 0 && (
                        <div className="mt-4 text-left border-t pt-4 w-full">
                          <h4 className="text-sm font-medium text-gray-700">Selected files:</h4>
                          <ul className="mt-2 divide-y divide-gray-100">
                            {selectedFiles.map((file, index) => (
                              <li key={index} className="text-sm text-gray-500 py-1">
                                {file.name} ({(file.size / 1024 / 1024).toFixed(2)} MB)
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          )}

          {currentStep === 1 && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-50 bg-gray-50/50 flex items-center gap-3">
              <Truck className="w-5 h-5 text-indigo-500" />
              <h3 className="text-lg font-semibold text-gray-800">Receiving Details</h3>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                <div className="col-span-2 md:col-span-1">
                  <ContactCombobox
                    label="Sender Name/Designation *"
                    value={formData.sender}
                    onSelect={(contact) => {
                       const name = contact.contactPerson || contact.organizationName || contact.email;
                       setFormData(prev => ({
                         ...prev,
                         sender: name,
                         address: contact.address || prev.address
                       }));
                       setIsAddressReadOnly(!!contact.address);
                       if (errors.sender) setErrors(prev => ({ ...prev, sender: null }));
                    }}
                    error={errors.sender}
                    placeholder="Search or Select Sender..."
                    className="transition-all duration-200"
                  />
                </div>

                <div className="col-span-2 md:col-span-1">
                  <Textarea
                    label="Sender Address"
                    id="address"
                    name="address"
                    rows="3"
                    value={formData.address}
                    onChange={handleChange}
                    placeholder="Sender's full address"
                    className="transition-all duration-200 focus:ring-2 focus:ring-indigo-100"
                    disabled={isAddressReadOnly}
                  />
                  {isAddressReadOnly && (
                    <button 
                        type="button" 
                        onClick={() => setIsAddressReadOnly(false)}
                        className="text-xs text-indigo-600 hover:text-indigo-800 mt-1"
                    >
                        Edit Address
                    </button>
                  )}
                </div>

                <div>
                  <Input
                    label="Reference Number"
                    id="referenceNumber"
                    name="referenceNumber"
                    value={formData.referenceNumber}
                    onChange={handleChange}
                    placeholder="Enter reference number"
                    className="transition-all duration-200 focus:ring-2 focus:ring-indigo-100"
                  />
                </div>

                <div>
                  <Input
                    type="date"
                    label="Dated"
                    id="dateOfLetter"
                    name="dateOfLetter"
                    value={formData.dateOfLetter}
                    onChange={handleChange}
                    className="transition-all duration-200 focus:ring-2 focus:ring-indigo-100"
                  />
                </div>

                <div>
                  <Combobox
                    label="Mode of Receipt"
                    id="mode"
                    name="mode"
                    icon={Truck}
                    value={formData.mode}
                    onChange={(val) => handleChange({ target: { name: 'mode', value: val } })}
                    className="transition-all duration-200 focus:ring-2 focus:ring-indigo-100"
                    options={(settings.receipt_modes || []).map(mode => ({ value: mode, label: mode }))}
                    placeholder="Select Mode"
                  />
                </div>

                <div>
                  <Combobox
                    label="Priority"
                    id="priority"
                    name="priority"
                    icon={AlertTriangle}
                    value={formData.priority}
                    onChange={(val) => handleChange({ target: { name: 'priority', value: val } })}
                    className="transition-all duration-200 focus:ring-2 focus:ring-indigo-100"
                    options={(settings.receipt_priorities || []).map(prio => ({ value: prio, label: prio }))}
                    placeholder="Select Priority"
                  />
                </div>

                <div className="col-span-2">
                  <label className="label">Other Details</label>
                  <RichTextEditor
                    value={formData.remarks}
                    onChange={(html) => setFormData(prev => ({ ...prev, remarks: html }))}
                    placeholder="Enter any other receiving details..."
                    className="transition-all duration-200"
                  />
                </div>
              </div>
            </div>
          </div>
          )}

        </form>

        {isEdit && <CaseHistory caseType="receipt" caseId={id} onPageClick={handlePageClick} />}
      </div>

      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={() => setConfirmDialog(prev => ({ ...prev, isOpen: false }))}
        onConfirm={confirmDialog.onConfirm}
        title={confirmDialog.title}
        message={confirmDialog.message}
        confirmText={confirmDialog.confirmText}
        variant={confirmDialog.variant}
      />

      <FilePreviewModal 
        file={previewFile} 
        isOpen={isPreviewOpen} 
        onClose={() => setIsPreviewOpen(false)} 
        initialPage={initialPage}
      />
    </>
  );
};

export default ReceiptForm;
